import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Star, Trash2 } from 'lucide-react';
import { useCreateContact, useUpdateContact } from '../../hooks/useContacts.js';
import { useCompanies } from '../../hooks/useCompanies.js';
import { useFieldDefinitions } from '../../hooks/useFieldDefinitions.js';
import { useValidationRules } from '../../hooks/useValidationRules.js';
import { runValidationRules } from '../../lib/validationEngine.js';
import { toast } from '../../lib/toast.js';
import CustomFieldInput from '../customFields/CustomFieldInput.js';
import type { Contact } from '@mayksel/types';

interface Props {
  contact?: Contact | null;
  /** Pre-link to a company when opened from a company detail page */
  defaultCompanyId?: string | undefined;
  onClose: () => void;
}

interface CompanyLink {
  companyId: string;
  companyName: string;
  isPrimary: boolean;
  role: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  mobile: string;
  notes: string;
  companies: CompanyLink[];
}

export default function ContactModal({ contact, defaultCompanyId, onClose }: Props) {
  const { t } = useTranslation('contacts');
  const overlayRef = useRef<HTMLDivElement>(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const createContact = useCreateContact();
  const updateContact = useUpdateContact(contact?.id ?? '');
  const { data: customFieldDefs = [] } = useFieldDefinitions('contact');
  const { data: validationRulesList = [] } = useValidationRules('contact');
  const activeCustomFields = customFieldDefs.filter((f) => f.isActive);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    contact?.customFields ?? {},
  );

  // Load companies for the association picker
  const { data: companiesData } = useCompanies({
    ...(companySearch ? { search: companySearch } : {}),
    limit: 20,
  });

  const [form, setForm] = useState<FormState>(() => {
    const initialCompanies: CompanyLink[] = contact?.companies?.map((c) => ({
      companyId: c.companyId,
      companyName: c.companyName,
      isPrimary: c.isPrimary,
      role: c.role ?? '',
    })) ?? [];

    // If opened from a company detail page with no existing contact, pre-link
    if (!contact && defaultCompanyId && companiesData?.data) {
      const co = companiesData.data.find((c) => c.id === defaultCompanyId);
      if (co) {
        initialCompanies.push({ companyId: co.id, companyName: co.name, isPrimary: true, role: '' });
      }
    }

    return {
      firstName: contact?.firstName ?? '',
      lastName: contact?.lastName ?? '',
      title: contact?.title ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      mobile: contact?.mobile ?? '',
      notes: contact?.notes ?? '',
      companies: initialCompanies,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-link defaultCompany once company data loads (for new contacts)
  useEffect(() => {
    if (!contact && defaultCompanyId && companiesData?.data && form.companies.length === 0) {
      const co = companiesData.data.find((c) => c.id === defaultCompanyId);
      if (co) {
        setForm((prev) => ({
          ...prev,
          companies: [{ companyId: co.id, companyName: co.name, isPrimary: true, role: '' }],
        }));
      }
    }
  }, [contact, defaultCompanyId, companiesData, form.companies.length]);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function addCompanyLink(companyId: string, companyName: string) {
    if (form.companies.some((c) => c.companyId === companyId)) return;
    const isFirst = form.companies.length === 0;
    setForm((prev) => ({
      ...prev,
      companies: [...prev.companies, { companyId, companyName, isPrimary: isFirst, role: '' }],
    }));
    setCompanySearch('');
    setShowCompanyDropdown(false);
  }

  function removeCompanyLink(companyId: string) {
    setForm((prev) => {
      const remaining = prev.companies.filter((c) => c.companyId !== companyId);
      // If we removed the primary, assign primary to first remaining
      if (!remaining.some((c) => c.isPrimary) && remaining.length > 0) {
        remaining[0] = { ...remaining[0]!, isPrimary: true };
      }
      return { ...prev, companies: remaining };
    });
  }

  function setPrimary(companyId: string) {
    setForm((prev) => ({
      ...prev,
      companies: prev.companies.map((c) => ({ ...c, isPrimary: c.companyId === companyId })),
    }));
  }

  function updateRole(companyId: string, role: string) {
    setForm((prev) => ({
      ...prev,
      companies: prev.companies.map((c) => c.companyId === companyId ? { ...c, role } : c),
    }));
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const trigger = contact ? 'on_update' : 'on_create';
    const entityData: Record<string, unknown> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      title: form.title.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      mobile: form.mobile.trim(),
      notes: form.notes.trim(),
      ...customFieldValues,
    };
    const violations = runValidationRules(validationRulesList, entityData, trigger);
    const blocking = violations.filter((v) => v.actionType !== 'show_warning');
    if (blocking.length > 0) {
      blocking.forEach((v) => toast.error(v.message));
      return;
    }
    violations.filter((v) => v.actionType === 'show_warning').forEach((v) => toast.error(v.message));

    const data = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      title: form.title.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      notes: form.notes.trim() || undefined,
      customFields: customFieldValues,
      companies: form.companies.map((c) => ({
        companyId: c.companyId,
        isPrimary: c.isPrimary,
        role: c.role.trim() || undefined,
      })),
    };

    try {
      if (contact) {
        await updateContact.mutateAsync(data);
        toast.success(t('toast.updated'));
      } else {
        await createContact.mutateAsync(data);
        toast.success(t('toast.created'));
      }
      onClose();
    } catch (_err) {
      toast.error(contact ? t('toast.update_error') : t('toast.create_error'));
    }
  }

  const isPending = createContact.isPending || updateContact.isPending;

  // Companies not yet linked (for the dropdown)
  const linkedIds = new Set(form.companies.map((c) => c.companyId));
  const availableCompanies = (companiesData?.data ?? []).filter((c) => !linkedIds.has(c.id));

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={contact ? t('form.edit_title') : t('form.create_title')}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--z-modal)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: 560, maxHeight: '90dvh', overflow: 'auto',
        boxShadow: 'var(--shadow-xl)', animation: 'page-enter 200ms var(--ease-out)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
            {contact ? t('form.edit_title') : t('form.create_title')}
          </h2>
          <button onClick={onClose} aria-label={t('actions.close', { ns: 'common' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

            {/* Name row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.first_name_label')} error={errors.firstName} required>
                <input type="text" value={form.firstName} autoFocus
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder={t('form.first_name_placeholder')} style={inputStyle(!!errors.firstName)} />
              </Field>
              <Field label={t('form.last_name_label')} error={errors.lastName} required>
                <input type="text" value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder={t('form.last_name_placeholder')} style={inputStyle(!!errors.lastName)} />
              </Field>
            </div>

            {/* Title */}
            <Field label={t('form.title_label')}>
              <input type="text" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t('form.title_placeholder')} style={inputStyle(false)} />
            </Field>

            {/* Email + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.email_label')} error={errors.email}>
                <input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={inputStyle(!!errors.email)} />
              </Field>
              <Field label={t('form.phone_label')}>
                <input type="tel" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  style={inputStyle(false)} />
              </Field>
            </div>

            {/* Mobile */}
            <Field label={t('form.mobile_label')}>
              <input type="tel" value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                style={inputStyle(false)} />
            </Field>

            {/* Companies */}
            <div>
              <label style={labelStyle}>{t('form.companies_label')}</label>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2, marginBottom: 'var(--space-3)' }}>
                {t('form.companies_hint')}
              </p>

              {/* Linked companies list */}
              {form.companies.length > 0 && (
                <div style={{
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                  overflow: 'hidden', marginBottom: 'var(--space-3)',
                }}>
                  {form.companies.map((link) => (
                    <div key={link.companyId} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto auto',
                      alignItems: 'center', gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      borderBottom: '1px solid var(--color-gray-100)',
                      background: link.isPrimary ? 'var(--color-brand-pale)' : 'var(--color-bg)',
                    }}>
                      <div>
                        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                          {link.companyName}
                          {link.isPrimary && (
                            <span style={{
                              marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)',
                              color: 'var(--color-brand)', fontWeight: 'var(--font-medium)',
                            }}>
                              {t('detail.primary_badge')}
                            </span>
                          )}
                        </p>
                        <input
                          type="text" value={link.role}
                          onChange={(e) => updateRole(link.companyId, e.target.value)}
                          placeholder={t('form.role_placeholder')}
                          style={{
                            marginTop: 2, width: '100%', border: 'none', background: 'transparent',
                            fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)',
                            padding: 0, outline: 'none',
                          }}
                        />
                      </div>
                      <button type="button" onClick={() => setPrimary(link.companyId)}
                        disabled={link.isPrimary}
                        title={t('form.primary_label')}
                        aria-label={`Mark ${link.companyName} as primary`}
                        style={{
                          background: 'none', border: 'none', cursor: link.isPrimary ? 'default' : 'pointer',
                          color: link.isPrimary ? 'var(--color-brand)' : 'var(--color-gray-300)',
                          display: 'flex', padding: 4,
                        }}>
                        <Star size={14} fill={link.isPrimary ? 'currentColor' : 'none'} />
                      </button>
                      <button type="button" onClick={() => removeCompanyLink(link.companyId)}
                        aria-label={`Remove ${link.companyName}`}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-gray-400)', display: 'flex', padding: 4,
                        }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add company search */}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Plus size={14} style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--color-text-muted)', pointerEvents: 'none',
                  }} />
                  <input
                    type="text" value={companySearch}
                    onChange={(e) => { setCompanySearch(e.target.value); setShowCompanyDropdown(true); }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                    placeholder={t('form.add_company')}
                    style={{
                      ...inputStyle(false), paddingLeft: 30,
                      fontSize: 'var(--text-sm)',
                    }}
                  />
                </div>

                {showCompanyDropdown && availableCompanies.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
                    zIndex: 'var(--z-dropdown)', maxHeight: 200, overflow: 'auto',
                    marginTop: 2,
                  }}>
                    {availableCompanies.map((co) => (
                      <button
                        key={co.id} type="button"
                        onMouseDown={() => addCompanyLink(co.id, co.name)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--text-sm)', color: 'var(--color-text)',
                          background: 'none', border: 'none', cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        {co.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <Field label={t('form.notes_label')}>
              <textarea value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                style={{ ...inputStyle(false), resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
            </Field>
          </div>

          {/* Custom Fields */}
          {activeCustomFields.length > 0 && (
            <div style={{ padding: '0 var(--space-6) var(--space-4)' }}>
              <p className="label" style={{ marginBottom: 'var(--space-3)' }}>
                {t('section_custom_fields', { ns: 'customFields' })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {activeCustomFields.map((field) => (
                  <Field key={field.id} label={field.label}>
                    <CustomFieldInput
                      field={field}
                      value={customFieldValues[field.key]}
                      onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: val }))}
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end',
            padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border)',
          }}>
            <button type="button" onClick={onClose} disabled={isPending} style={cancelBtnStyle}>
              {t('actions.cancel', { ns: 'common' })}
            </button>
            <button type="submit" disabled={isPending} style={{
              ...saveBtnStyle,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.7 : 1,
            }}>
              {isPending ? t('actions.loading', { ns: 'common' }) : t('actions.save', { ns: 'common' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, error, required }: {
  label: string; children: React.ReactNode; error?: string | undefined; required?: boolean | undefined;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      </label>
      <div style={{ marginTop: 'var(--space-1)' }}>{children}</div>
      {error && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
  color: 'var(--color-text)', display: 'block',
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%', padding: 'var(--space-2) var(--space-3)',
    border: hasError ? '1px solid var(--color-danger)' : '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
    color: 'var(--color-text)', background: 'var(--color-bg)', boxSizing: 'border-box',
  };
}

const cancelBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', background: 'var(--color-bg)', cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)', border: 'none',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
};
